import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiGet, apiDownload } from '../api/client';
import type { VaultResponse, HeirloomListResponse, HeirloomResponse } from '../types/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './PrintVaultPage.css';

export default function PrintHeirloomsPage() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vault, setVault] = useState<VaultResponse | null>(null);
  const [heirlooms, setHeirlooms] = useState<HeirloomResponse[]>([]);
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});
  const [photosReady, setPhotosReady] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const [vaultData, heirloomData] = await Promise.all([
          apiGet<VaultResponse>(`/api/vault/${id}`),
          apiGet<HeirloomListResponse>(`/api/vault/${id}/heirlooms`),
        ]);
        setVault(vaultData);
        setHeirlooms(heirloomData.heirlooms);
      } catch (err: any) {
        setError(err.message || 'Failed to load heirlooms.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Load thumbnails for heirlooms that have photos, then mark photos ready.
  useEffect(() => {
    const withPhotos = heirlooms.filter(h => h.hasPhoto);
    if (withPhotos.length === 0) {
      setPhotosReady(true);
      return;
    }

    let completed = 0;
    const urls: Record<string, string> = {};

    withPhotos.forEach(async (h) => {
      try {
        const { blob } = await apiDownload(`/api/vault/${id}/heirlooms/${h.id}/photo`);
        urls[h.id] = URL.createObjectURL(blob);
      } catch {
        // Photo failed — leave cell empty, still counts as done
      } finally {
        completed++;
        if (completed === withPhotos.length) {
          setThumbUrls(urls);
          setPhotosReady(true);
        }
      }
    });
  }, [heirlooms, id]);

  const printDate = new Date().toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });

  if (loading) return <LoadingSpinner fullPage />;

  if (error) {
    return (
      <div className="print-vault-page">
        <p className="print-vault-error">{error}</p>
        <Link to="/">&larr; Back to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="print-vault-page">
      <div className="print-vault-actions no-print">
        <button
          className="btn-primary"
          onClick={() => window.print()}
          disabled={!photosReady}
        >
          {photosReady ? 'Print / Save as PDF' : 'Loading photos…'}
        </button>
        <Link to="/" className="btn-text">&larr; Back to Vault</Link>
      </div>

      <div className="print-vault-content">
        <header className="print-vault-header">
          <h1>Possessions &mdash; {vault?.name}</h1>
          <p className="print-vault-date">Printed {printDate}</p>
          <p className="print-vault-date" style={{ marginTop: 6 }}>
            This is a record of personal items in this vault and their intended disposition.
            Keep this list with your will or in a safe place your family can find.
          </p>
        </header>

        {heirlooms.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
            No possessions have been recorded in this vault yet.
          </p>
        ) : (
          <section className="print-section">
            <table className="print-table">
              <thead>
                <tr>
                  <th style={{ width: '2.5rem' }}>#</th>
                  <th style={{ width: '64px' }}>Photo</th>
                  <th style={{ width: '28%' }}>Item</th>
                  <th>Notes &amp; Instructions</th>
                </tr>
              </thead>
              <tbody>
                {heirlooms.map((h, i) => (
                  <tr key={h.id}>
                    <td style={{ color: 'var(--text-muted)', textAlign: 'center', verticalAlign: 'middle' }}>{i + 1}</td>
                    <td style={{ padding: '6px 8px', verticalAlign: 'middle' }}>
                      {thumbUrls[h.id] ? (
                        <img
                          src={thumbUrls[h.id]}
                          alt={h.title}
                          style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, display: 'block' }}
                        />
                      ) : null}
                    </td>
                    <td style={{ fontWeight: 500, verticalAlign: 'middle' }}>{h.title}</td>
                    <td style={{ whiteSpace: 'pre-wrap', verticalAlign: 'middle' }}>{h.instructions || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <footer className="print-vault-footer">
          Confidential &mdash; Printed from Cedar Vault on {printDate}
        </footer>
      </div>
    </div>
  );
}
